/**
 * Block-level three-way merge (diff3).
 *
 * Blocks are atomic units matched by content (string equality == content
 * hash).  Given a common ancestor `base` and two diverged sequences `ours`
 * and `theirs`, the merge:
 *
 *   - keeps blocks unchanged on both sides;
 *   - auto-merges regions changed on only one side;
 *   - surfaces regions changed on both sides as conflicts.
 *
 * No block IDs are used — matching is by content only, so a move/reorder is
 * seen as "delete + insert" (a known limitation) and duplicate blocks match
 * ambiguously (also known).
 */

const _DP_MAX_CELLS = 4_000_000; // fall back to greedy LCS past ~4M cells

// ── LCS matching ────────────────────────────────────────────────

/**
 * Return matched index pairs `[[bi, si], ...]` between `a` and `b`, sorted by
 * `bi` and monotonic in `si`.  Uses DP for small inputs, a greedy hash-based
 * matcher for large ones (memory guard — notes are normally small).
 */
function _lcsPairs(a, b) {
  if (a.length === 0 || b.length === 0) return [];
  if (a.length * b.length <= _DP_MAX_CELLS) return _lcsDp(a, b);
  return _lcsGreedy(a, b);
}

function _lcsDp(a, b) {
  const n = a.length;
  const m = b.length;
  const dp = new Array(n + 1);
  dp[0] = new Int32Array(m + 1);
  for (let i = 1; i <= n; i++) {
    const row = new Int32Array(m + 1);
    const prev = dp[i - 1];
    const ai = a[i - 1];
    for (let j = 1; j <= m; j++) {
      row[j] = ai === b[j - 1] ? prev[j - 1] + 1 : Math.max(prev[j], row[j - 1]);
    }
    dp[i] = row;
  }

  const pairs = [];
  let i = n;
  let j = m;
  while (i > 0 && j > 0) {
    if (a[i - 1] === b[j - 1]) {
      pairs.push([i - 1, j - 1]);
      i--;
      j--;
    } else if (dp[i - 1][j] >= dp[i][j - 1]) {
      i--;
    } else {
      j--;
    }
  }
  return pairs.reverse();
}

function _lcsGreedy(a, b) {
  const idx = new Map();
  for (let j = 0; j < b.length; j++) {
    const list = idx.get(b[j]);
    if (list) list.push(j);
    else idx.set(b[j], [j]);
  }
  const pairs = [];
  let bCursor = 0;
  for (let i = 0; i < a.length; i++) {
    const positions = idx.get(a[i]);
    if (!positions) continue;
    // first position >= bCursor (binary search)
    let lo = 0;
    let hi = positions.length;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (positions[mid] < bCursor) lo = mid + 1;
      else hi = mid;
    }
    if (lo < positions.length) {
      pairs.push([i, positions[lo]]);
      bCursor = positions[lo] + 1;
    }
  }
  return pairs;
}

// ── Change hunks ────────────────────────────────────────────────

/**
 * Diff `side` against `base`, returning sorted, non-overlapping change hunks.
 * Each hunk is `{ s, e, repl }`: base range `[s, e)` (s === e for a pure
 * insertion) is replaced by `repl` (a slice of `side`).
 */
function _changes(base, side) {
  const pairs = _lcsPairs(base, side);
  const hunks = [];
  let b = 0;
  let s = 0;
  for (const [bi, si] of pairs) {
    if (bi > b || si > s) {
      hunks.push({ s: b, e: bi, repl: side.slice(s, si) });
    }
    b = bi + 1;
    s = si + 1;
  }
  if (b < base.length || s < side.length) {
    hunks.push({ s: b, e: base.length, repl: side.slice(s) });
  }
  return hunks;
}

// ── Merge ───────────────────────────────────────────────────────

function _blocksEqual(a, b) {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}

/**
 * Merge two sorted change-hunk lists over the base axis.
 *
 * @returns {{segments: Array, autoMergedCount: number}}
 *   Each segment is `{ type: 'auto', blocks }` (auto-merged) or
 *   `{ type: 'conflict', ours, theirs }`.
 */
function _mergeHunks(base, oc, tc) {
  const segments = [];
  let autoMergedCount = 0;

  const pushAuto = (blocks) => {
    if (blocks.length === 0) return;
    const last = segments[segments.length - 1];
    if (last && last.type === 'auto') {
      last.blocks = last.blocks.concat(blocks);
    } else {
      segments.push({ type: 'auto', blocks: blocks.slice() });
    }
  };

  const pushConflict = (ours, theirs) => {
    if (_blocksEqual(ours, theirs)) {
      pushAuto(ours);
      autoMergedCount++;
      return;
    }
    segments.push({ type: 'conflict', ours: ours.slice(), theirs: theirs.slice() });
  };

  let oi = 0;
  let ti = 0;
  let b = 0;
  const n = base.length;

  while (oi < oc.length || ti < tc.length) {
    const o = oi < oc.length ? oc[oi] : null;
    const t = ti < tc.length ? tc[ti] : null;

    const oStart = o ? o.s : Infinity;
    const tStart = t ? t.s : Infinity;
    const nextStart = Math.min(oStart, tStart);

    // Emit untouched base blocks up to the next change.
    while (b < nextStart && b < n) {
      pushAuto([base[b]]);
      b++;
    }

    if (o && t && o.s === b && t.s === b) {
      // Both sides changed at this base point.
      pushConflict(o.repl, t.repl);
      b = Math.max(o.e, t.e);
      oi++;
      ti++;
      continue;
    }

    if (o && o.s === b) {
      // Ours changes at b — check for a partial overlap with theirs.
      if (t && t.s < o.e) {
        pushConflict(o.repl, t.repl);
        b = Math.max(o.e, t.e);
        oi++;
        ti++;
      } else {
        pushAuto(o.repl);
        autoMergedCount++;
        b = o.e;
        oi++;
      }
      continue;
    }

    if (t && t.s === b) {
      // Theirs changes at b — check for a partial overlap with ours.
      if (o && o.s < t.e) {
        pushConflict(o.repl, t.repl);
        b = Math.max(o.e, t.e);
        oi++;
        ti++;
      } else {
        pushAuto(t.repl);
        autoMergedCount++;
        b = t.e;
        ti++;
      }
      continue;
    }

    // Neither starts at b (e.g. a hunk already consumed but its end > b).
    // Safety net — advance the earliest-starting side.
    if (oStart <= tStart) oi++;
    else ti++;
  }

  while (b < n) {
    pushAuto([base[b]]);
    b++;
  }

  return { segments, autoMergedCount };
}

// ── Public API ──────────────────────────────────────────────────

/**
 * Three-way merge of block arrays.
 *
 * @param {string[]} base - Common ancestor blocks (may be empty → 2-way).
 * @param {string[]} ours - Our diverged blocks.
 * @param {string[]} theirs - Their diverged blocks.
 * @returns {{segments: Array<{type: 'auto', blocks: string[]} | {type: 'conflict', ours: string[], theirs: string[]}>, autoMergedCount: number}}
 */
export function diff3Blocks(base, ours, theirs) {
  const oc = _changes(base, ours);
  const tc = _changes(base, theirs);
  return _mergeHunks(base, oc, tc);
}
